/**
 * Product Reel Lazy Loader
 * Progressively loads products as user scrolls to improve initial page load performance
 */
class ProductReelLazyLoader extends HTMLElement {
    constructor() {
        super();

        this.container = this.querySelector('[data-drag-parent]');
        this.dataScript = this.querySelector('[data-lazy-products]');



        if (!this.dataScript) {
            // No lazy loading data, all products already rendered
            return;
        }

        try {
            this.config = JSON.parse(this.dataScript.textContent);

            // Detect if mobile
            this.isMobile = window.innerWidth <= 768;

            // Use mobile-specific settings if on mobile
            if (this.isMobile) {
                if (this.config.initialCountMobile !== undefined) {
                    this.config.initialCount = this.config.initialCountMobile;
                }
                if (this.config.batchSizeMobile !== undefined) {
                    this.config.batchSize = this.config.batchSizeMobile;
                }
            }


        } catch (e) {
            console.error('[ProductReelLazyLoader] Failed to parse lazy load data:', e);
            return;
        }

        this.loadedCount = this.config.initialCount;
        this.currentBatch = 0;
        this.isLoading = false;

        this.initVideoObserver();
        this.initSlideshowObserver();
        this.initIntersectionObserver();
        this.initTappedObserver();
        this.initDragToScroll();
    }

    initTappedObserver() {
        // Watch for 'is-tapped' class changes on card-media elements
        // This ensures video plays when user taps the card (which toggles is-tapped via theme.js)
        this.tappedObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    const target = mutation.target;
                    if (target.classList.contains('is-tapped')) {
                        this.handleTappedState(target);
                    }
                }
            });
        });

        // Observe existing cards
        this.observeTappedCards();
    }

    observeTappedCards() {
        if (!this.tappedObserver) return;
        const cardMedias = this.container.querySelectorAll('.card-media');
        cardMedias.forEach(media => {
            this.tappedObserver.observe(media, { attributes: true, attributeFilter: ['class'] });
        });
    }

    handleTappedState(cardMedia) {
        const videoWrapper = cardMedia.querySelector('[data-lazy-video]');
        if (!videoWrapper) return;

        // Ensure video is initialized
        if (!videoWrapper.classList.contains('video-loaded') && !videoWrapper.classList.contains('video-loading')) {
            this.initializeVideo(videoWrapper);
        }

        // Force play if paused
        // Use a small timeout to allow display:none/opacity transition to complete if needed
        setTimeout(() => {
            const video = videoWrapper.querySelector('video');
            if (video && video.paused) {
                video.play().catch(e => {
                    // Autoplay might fail if not muted or user interaction required
                    // But since this is triggered by a user tap (indirectly), it should work
                    console.debug('Auto-play on tap failed:', e);
                });
                videoWrapper.classList.remove('paused');
            }
        }, 50);
    }

    initVideoObserver() {
        // Observe all lazy video elements and initialize them when visible
        const videoOptions = {
            root: null, // Use viewport as root
            rootMargin: '300px', // Start loading well before entering viewport for smoother UX
            threshold: 0.1
        };

        this.videoObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const videoWrapper = entry.target;
                    if (!videoWrapper.classList.contains('video-loaded') &&
                        !videoWrapper.classList.contains('video-loading')) {
                        this.initializeVideo(videoWrapper);
                    }
                }
            });
        }, videoOptions);

        // Observe all existing lazy videos
        this.observeVideos();
    }

    observeVideos() {
        const lazyVideos = this.container.querySelectorAll('[data-lazy-video]');
        lazyVideos.forEach(video => {
            this.videoObserver.observe(video);

            // Add click handler for tap-to-play
            // Remove existing listener first to avoid duplicates
            video.removeEventListener('click', this.handleVideoClick);
            video.addEventListener('click', this.handleVideoClick.bind(this));

            // Add touchend for better mobile support (in case click is swallowed by slider)
            video.removeEventListener('touchend', this.handleVideoTouchEnd);
            video.addEventListener('touchstart', this.handleVideoTouchStart.bind(this), { passive: true });
            video.addEventListener('touchend', this.handleVideoTouchEnd.bind(this));
        });
    }

    handleVideoTouchStart(e) {
        this.touchStartX = e.changedTouches[0].screenX;
        this.touchStartY = e.changedTouches[0].screenY;
    }

    handleVideoTouchEnd(e) {
        const touchEndX = e.changedTouches[0].screenX;
        const touchEndY = e.changedTouches[0].screenY;

        // Calculate distance moved
        const diffX = Math.abs(touchEndX - this.touchStartX);
        const diffY = Math.abs(touchEndY - this.touchStartY);

        // If moved less than 10px, consider it a tap
        if (diffX < 10 && diffY < 10) {
            // Prevent default to avoid double-firing with click if click also fires
            // But we need to be careful not to block scrolling if it was a scroll (handled by diff check)
            // e.preventDefault(); // Don't prevent default here, let click fire if it can, or call handler directly

            // Call handler directly
            this.handleVideoClick(e);
        }
    }

    handleVideoClick(e) {
        e.preventDefault();
        e.stopPropagation();

        const videoWrapper = e.currentTarget;

        if (!videoWrapper.classList.contains('video-loaded') && !videoWrapper.classList.contains('video-loading')) {
            // Start loading if not already
            this.initializeVideo(videoWrapper);
        } else {
            // Toggle play/pause
            const videoEl = videoWrapper.querySelector('video, iframe');
            if (videoEl) {
                if (videoEl.tagName === 'VIDEO') {
                    if (videoEl.paused) {
                        videoEl.play();
                        videoWrapper.classList.remove('paused');
                    } else {
                        videoEl.pause();
                        videoWrapper.classList.add('paused');
                    }
                } else if (videoEl.tagName === 'IFRAME') {
                    // For iframe, we can't easily toggle play/pause without postMessage API
                    // For now, just let it be or implement postMessage if needed
                }
            }
        }
    }

    initializeVideo(videoWrapper) {
        videoWrapper.classList.add('video-loading');

        const videoType = videoWrapper.dataset.videoType || 'video/mp4';

        // Handle external videos (Vimeo/YouTube)
        if (videoType === 'external') {
            const embedUrl = videoWrapper.dataset.videoEmbedUrl;
            const videoHost = videoWrapper.dataset.videoHost;

            if (!embedUrl) {
                console.error('No embed URL for external video');
                videoWrapper.classList.remove('video-loading');
                return;
            }

            // Create iframe element
            const iframe = document.createElement('iframe');
            iframe.src = embedUrl;
            iframe.setAttribute('frameborder', '0');
            iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
            iframe.setAttribute('allowfullscreen', '');
            // CSS controls sizing for cover fit (see product-block-reel.liquid)
            // iframe.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%;';

            // Mark as loaded immediately (autoplay is handled by URL params)
            videoWrapper.classList.remove('video-loading');
            videoWrapper.classList.add('video-loaded');

            // Append iframe to wrapper
            videoWrapper.appendChild(iframe);
            return;
        }

        // Handle native Shopify videos
        const videoSrc = videoWrapper.dataset.videoSrc;
        const videoPoster = videoWrapper.dataset.videoPoster;
        const muted = videoWrapper.dataset.videoMuted === 'true';
        const loop = videoWrapper.dataset.videoLoop === 'true';
        const autoplay = videoWrapper.dataset.videoAutoplay === 'true';
        const playsinline = videoWrapper.dataset.videoPlaysinline === 'true';

        if (!videoSrc) {

            return;
        }

        // Create video element
        const video = document.createElement('video');
        video.muted = true; // Always mute for autoplay to work
        video.setAttribute('muted', ''); // Set attribute for Safari
        video.loop = loop;
        video.playsInline = playsinline;
        video.poster = videoPoster;
        video.preload = 'metadata'; // Load metadata and first frame faster

        if (autoplay) {
            video.autoplay = true;
        }

        // Add source
        const source = document.createElement('source');
        source.src = videoSrc;
        source.type = videoType;
        video.appendChild(source);

        // Handle video load
        video.addEventListener('loadeddata', () => {
            videoWrapper.classList.remove('video-loading');
            videoWrapper.classList.add('video-loaded');

            // Try to play if autoplay is enabled
            if (autoplay) {
                video.play().catch(e => {

                });
            }
        });

        // Handle errors
        video.addEventListener('error', () => {
            console.error('Error loading video:', videoSrc);
            videoWrapper.classList.remove('video-loading');
        });

        // Append video to wrapper
        videoWrapper.appendChild(video);

        // Stop observing this video
        this.videoObserver.unobserve(videoWrapper);
    }

    initSlideshowObserver() {
        const options = {
            root: null,
            threshold: 0.5
        };

        this.slideshowObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.startSlideshow(entry.target);
                } else {
                    this.stopSlideshow(entry.target);
                }
            });
        }, options);

        this.observeSlideshows();
    }

    observeSlideshows() {
        const slideshows = this.container.querySelectorAll('.slideshow-wrapper');
        slideshows.forEach(slideshow => {
            this.slideshowObserver.observe(slideshow);
        });
    }

    getSlideshowSlides(wrapper) {
        let slides = Array.from(wrapper.querySelectorAll('.slideshow-slide'));

        // Backward-compatible fallback: normalize slides missing class (legacy/invalid markup).
        if (!slides.length) {
            slides = Array.from(wrapper.querySelectorAll('img'));
            slides.forEach((slide, index) => {
                slide.classList.add('slideshow-slide');
                slide.classList.toggle('active', index === 0);
            });
        } else if (!slides.some((slide) => slide.classList.contains('active'))) {
            slides[0].classList.add('active');
        }

        return slides;
    }

    startSlideshow(wrapper) {
        if (wrapper.dataset.interval) return;

        const slides = this.getSlideshowSlides(wrapper);
        if (slides.length <= 1) return;

        let currentIndex = slides.findIndex((slide) => slide.classList.contains('active'));
        if (currentIndex === -1) currentIndex = 0;

        const interval = setInterval(() => {
            slides[currentIndex].classList.remove('active');
            currentIndex = (currentIndex + 1) % slides.length;
            slides[currentIndex].classList.add('active');
        }, 2500);

        wrapper.dataset.interval = interval;
    }

    stopSlideshow(wrapper) {
        if (wrapper.dataset.interval) {
            clearInterval(parseInt(wrapper.dataset.interval));
            delete wrapper.dataset.interval;
        }
    }

    initDragToScroll() {
        // Drag-to-scroll functionality from original ProductScroller
        const isOverflowing = (wrapper) => {
            return wrapper.clientWidth < wrapper.scrollWidth;
        };

        let containerOverflowing = isOverflowing(this.container);
        if (containerOverflowing) {
            this.dragToScroll(this.container);
        }
    }

    dragToScroll(slider) {
        let mouseDown = false;
        let start;
        let scrollLeft;
        let inactiveTimeout;

        slider.addEventListener('mousedown', (e) => {
            mouseDown = true;
            start = e.pageX - slider.offsetLeft;
            scrollLeft = slider.scrollLeft;
        });

        slider.addEventListener('mouseup', () => {
            mouseDown = false;
            clearTimeout(inactiveTimeout);
            inactiveTimeout = setTimeout(() => {
                slider.classList.remove('active');
            }, 150);
        });

        slider.addEventListener('mousemove', (e) => {
            if (!mouseDown) return;
            e.preventDefault();

            if (!slider.classList.contains('active')) {
                slider.classList.add('active');
            }

            const x = e.pageX - slider.offsetLeft;
            const walk = (x - start) * 1;
            slider.scrollLeft = scrollLeft - walk;
        });

        slider.addEventListener('mouseleave', () => {
            mouseDown = false;
            clearTimeout(inactiveTimeout);
            inactiveTimeout = setTimeout(() => {
                slider.classList.remove('active');
            }, 150);
        });
    }

    initIntersectionObserver() {


        // Remove old sentinel if exists
        const oldSentinel = this.container.querySelector('.lazy-load-sentinel');
        if (oldSentinel) oldSentinel.remove();

        // Bind the scroll handler with debouncing
        let scrollTimeout;
        this.onScroll = () => {
            if (scrollTimeout) return;
            scrollTimeout = setTimeout(() => {
                this.checkScroll();
                scrollTimeout = null;
            }, 100);
        };

        this.container.addEventListener('scroll', this.onScroll, { passive: true });

        // Also check immediately
        this.checkScroll();
    }

    checkScroll() {
        if (this.isLoading) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.container;
        const scrollRight = scrollLeft + clientWidth;
        const distanceToEnd = scrollWidth - scrollRight;



        // Load if we are within 600px of the end
        // Or if scrollWidth is close to clientWidth (meaning content barely fills or doesn't fill view)
        if (distanceToEnd < 600 || (scrollWidth - clientWidth) < 100) {

            this.loadNextBatch();
        }
    }

    async loadNextBatch() {


        if (this.isLoading) return;

        const remainingProducts = this.config.products.length - this.currentBatch * this.config.batchSize;


        if (remainingProducts <= 0) {
            // All products loaded

            if (this.onScroll) {
                this.container.removeEventListener('scroll', this.onScroll);
            }
            return;
        }

        this.isLoading = true;

        const startIndex = this.currentBatch * this.config.batchSize;
        const endIndex = Math.min(startIndex + this.config.batchSize, this.config.products.length);
        const batch = this.config.products.slice(startIndex, endIndex);



        // Render products
        const fragment = document.createDocumentFragment();
        batch.forEach((productData, index) => {
            const productElement = this.renderProduct(productData, this.loadedCount + index);
            fragment.appendChild(productElement);
        });

        // Append to container
        this.container.appendChild(fragment);

        this.loadedCount += batch.length;
        this.currentBatch++;
        this.isLoading = false;



        // Check if we need to load more immediately (e.g. if screen still not full)
        setTimeout(() => {
            this.checkScroll();
        }, 100);

        // Observe videos in newly loaded products
        this.observeVideos();
        this.observeSlideshows();
        this.observeTappedCards();
    }

    renderProduct(productData, index) {
        const productDiv = document.createElement('div');
        productDiv.className = `product${this.config.swipe === 'true' ? ' slider__slide grid__item' : ''}`;
        productDiv.setAttribute('data-product-index', index);
        productDiv.setAttribute('data-lazy-loaded', 'true');

        // Clone the last existing product element to maintain exact HTML structure
        const templateProduct = this.container.querySelector('.product:not([data-lazy-loaded])');
        if (templateProduct) {
            const clonedContent = templateProduct.querySelector('.product-item').cloneNode(true);
            this.updateProductData(clonedContent, productData);
            productDiv.appendChild(clonedContent);
        } else {
            // Fallback to generated HTML if no template exists
            productDiv.innerHTML = this.generateProductCardHTML(productData);
        }

        return productDiv;
    }

    updateProductData(element, product) {


        // Update product ID
        element.setAttribute('data-product-id', product.id);

        // Update all links
        const links = element.querySelectorAll('a');
        links.forEach(link => {
            link.href = product.url;
            link.title = product.title;
            if (link.hasAttribute('data-product-url')) {
                link.setAttribute('data-product-url', product.url);
            }
            if (link.hasAttribute('data-product-title')) {
                link.setAttribute('data-product-title', product.title);
            }
        });

        // Update title text
        const titleLink = element.querySelector('.card-title');
        if (titleLink) {
            titleLink.textContent = product.title;
        }

        // Update video or slideshow in card-product section
        const hasVideo = product.media && product.media.some(m => m.media_type === 'video' || m.media_type === 'external_video');
        const cardMediaVideo = element.querySelector('.card-product .card-media');
        const videoWrapper = cardMediaVideo?.querySelector('.video-wrapper');
        const slideshowWrapper = cardMediaVideo?.querySelector('.slideshow-wrapper');

        if (hasVideo) {
            const featuredMedia = product.media.find(m => m.media_type === 'video' || m.media_type === 'external_video');
            if (videoWrapper) {
                // Update existing video wrapper
                this.updateVideoWrapper(videoWrapper, product, featuredMedia);
            } else if (slideshowWrapper) {
                // Replace slideshow with video
                const newVideoHTML = this.generateVideoHTML(product, featuredMedia);
                slideshowWrapper.outerHTML = newVideoHTML;
                // Re-observe the new video
                const newVideoWrapper = cardMediaVideo.querySelector('.video-wrapper');
                if (newVideoWrapper) {
                    this.videoObserver.observe(newVideoWrapper);
                }
            }
        } else if (this.config.enableSlideshow) {
            if (slideshowWrapper) {
                // Update existing slideshow
                this.updateSlideshowWrapper(slideshowWrapper, product);
            } else if (videoWrapper) {
                // Replace video with slideshow
                const newSlideshowHTML = this.generateSlideshowHTML(product);
                videoWrapper.outerHTML = newSlideshowHTML;
                // Re-observe the new slideshow
                const newSlideshowWrapper = cardMediaVideo.querySelector('.slideshow-wrapper');
                if (newSlideshowWrapper) {
                    this.slideshowObserver.observe(newSlideshowWrapper);
                    // Manually start the slideshow since it's dynamically inserted
                    this.startSlideshow(newSlideshowWrapper);
                } else {
                    console.warn('[updateProductData] Could not find new slideshow wrapper after replacement');
                }
            }
        }

        // Update thumbnail images in card-information section
        const images = element.querySelectorAll('.product-card__column img');


        // Get first image from product media
        const firstImage = product.media?.find(m => m.media_type === 'image');
        const previewImage = firstImage?.preview_image?.src || firstImage?.src || product.featured_media?.preview_image?.src || '';



        images.forEach((img, idx) => {
            if (previewImage) {
                // Clear any lazy loading classes that might prevent update
                img.classList.remove('lazyload', 'lazyloaded', 'ls-is-cached');

                // Update both src and data-srcset
                img.src = previewImage;
                if (img.hasAttribute('data-srcset')) {
                    img.setAttribute('data-srcset', previewImage);
                }
                if (img.hasAttribute('srcset')) {
                    img.setAttribute('srcset', previewImage);
                }
                img.alt = product.title;

            }
        });

        // Update price
        const priceContainer = element.querySelector('.card-price');
        if (priceContainer) {
            priceContainer.innerHTML = this.generatePriceHTML(product);
        }
    }

    updateVideoWrapper(wrapper, product, media) {
        const isExternalVideo = media.media_type === 'external_video';
        const wrapperIsExternal = wrapper.classList.contains('external-video-wrapper');

        // If video type changed (native <-> external), regenerate the wrapper
        if (isExternalVideo !== wrapperIsExternal) {
            const cardMedia = wrapper.parentElement;
            const newVideoHTML = this.generateVideoHTML(product, media);
            wrapper.outerHTML = newVideoHTML;

            // Re-observe the new video wrapper
            const newWrapper = cardMedia.querySelector('.video-wrapper');
            if (newWrapper) {
                this.videoObserver.observe(newWrapper);
            }
            return;
        }

        const previewImage = media.preview_image?.src || '';

        if (isExternalVideo) {
            // Update external video (Vimeo/YouTube) attributes
            const videoHost = media.host;
            const videoId = media.external_id;

            let embedUrl = '';
            if (videoHost === 'youtube') {
                embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&playsinline=1&controls=0&modestbranding=1&rel=0`;
            } else if (videoHost === 'vimeo') {
                embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&autopause=0&playsinline=1&controls=0`;
            }

            wrapper.setAttribute('data-video-type', 'external');
            wrapper.setAttribute('data-video-host', videoHost);
            wrapper.setAttribute('data-video-embed-url', embedUrl);
            wrapper.setAttribute('data-video-poster', previewImage);

            // Remove any existing iframe to force re-initialization
            const existingIframe = wrapper.querySelector('iframe');
            if (existingIframe) {
                existingIframe.remove();
            }
        } else {
            // Update native video attributes
            const videoSrc = media.sources?.[0]?.url || '';

            wrapper.setAttribute('data-video-src', videoSrc);
            wrapper.setAttribute('data-video-poster', previewImage);
            wrapper.removeAttribute('data-video-type');
            wrapper.removeAttribute('data-video-host');
            wrapper.removeAttribute('data-video-embed-url');

            // Remove any existing video element to force re-initialization
            const existingVideo = wrapper.querySelector('video');
            if (existingVideo) {
                existingVideo.remove();
            }
        }

        // Remove loaded/loading classes to reset state
        wrapper.classList.remove('video-loaded', 'video-loading');

        // Update or create placeholder image
        let placeholder = wrapper.querySelector('.video-placeholder');
        if (placeholder) {
            placeholder.src = previewImage;
            placeholder.alt = product.title;
        } else {
            placeholder = document.createElement('img');
            placeholder.src = previewImage;
            placeholder.alt = product.title;
            placeholder.className = 'video-placeholder';
            wrapper.appendChild(placeholder);
        }

        // Re-observe to trigger video initialization
        this.videoObserver.unobserve(wrapper);
        this.videoObserver.observe(wrapper);
    }

    updateSlideshowWrapper(wrapper, product) {
        const wasRunning = !!wrapper.dataset.interval;
        this.stopSlideshow(wrapper);

        const limit = this.config.slideshowLength || 3;
        let images = product.media?.filter(m => m.media_type === 'image') || [];

        if (images.length === 0) return;

        images = images.slice(0, limit);

        // Clear existing slides
        wrapper.innerHTML = '';

        // Add new slides
        images.forEach((media, index) => {
            const src = media.preview_image?.src || media.src;
            if (!src) return;

            const img = document.createElement('img');
            img.src = src;
            img.alt = media.alt || product.title;
            img.className = `slideshow-slide${index === 0 ? ' active' : ''}`;
            img.loading = 'lazy';
            wrapper.appendChild(img);
        });

        if (wasRunning) {
            this.startSlideshow(wrapper);
        }
    }

    generateVideoHTML(product, media) {
        if (!media) return '';

        // Handle external videos (Vimeo/YouTube)
        if (media.media_type === 'external_video') {
            return this.generateExternalVideoHTML(product, media);
        }

        // Handle native Shopify videos
        if (media.media_type !== 'video') return '';

        const previewImage = media.preview_image?.src || '';

        // Find MP4 source first (robust check)
        let mp4Source = media.sources?.find(s => s.mime_type?.includes('video/mp4'));
        if (!mp4Source) {
            mp4Source = media.sources?.find(s => s.format === 'mp4');
        }

        // Fallback logic: Avoid HLS on non-Safari browsers
        let videoSrc = mp4Source?.url;
        if (!videoSrc && media.sources?.length) {
            const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
            const firstSource = media.sources[0];

            // If first source is HLS and we are NOT Safari, try to find another one
            if (firstSource.mime_type?.includes('application/x-mpegURL') && !isSafari) {
                const nonHlsSource = media.sources.find(s => !s.mime_type?.includes('application/x-mpegURL'));
                videoSrc = nonHlsSource?.url || ''; // If only HLS is available, we might fail to load, which is better than erroring
            } else {
                videoSrc = firstSource.url;
            }
        }

        // Use data attributes for lazy video initialization
        return `
            <div class="video-wrapper lazy-video" 
                 data-lazy-video 
                 data-video-src="${videoSrc}" 
                 data-video-poster="${previewImage}"
                 data-video-muted="true"
                 data-video-loop="true"
                 data-video-autoplay="true"
                 data-video-playsinline="true">
                <img src="${previewImage}" alt="${this.escapeHtml(product.title)}" class="video-placeholder">
                <div class="video-play-indicator" aria-hidden="true">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="32" cy="32" r="32" fill="rgba(255,255,255,0.95)"/>
                        <path d="M24 18L46 32L24 46V18Z" fill="#000"/>
                    </svg>
                </div>
            </div>
        `;
    }

    generateExternalVideoHTML(product, media) {
        const previewImage = media.preview_image?.src || media.preview_image || '';
        const videoHost = media.host; // 'youtube' or 'vimeo'
        const videoId = media.external_id;

        if (!videoId) {
            console.warn('[generateExternalVideoHTML] No external_id found for external video');
            return '';
        }

        let embedUrl = '';
        if (videoHost === 'youtube') {
            // YouTube embed URL with autoplay, muted, loop, and playsinline
            embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&playsinline=1&controls=0&modestbranding=1&rel=0`;
        } else if (videoHost === 'vimeo') {
            // Vimeo embed URL - removed background=1 to allow autoplay to work properly
            // background=1 mode doesn't support manual play/pause and can conflict with autoplay
            embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&autopause=0&playsinline=1&controls=0`;
        } else {
            console.warn('[generateExternalVideoHTML] Unknown video host:', videoHost);
            return '';
        }

        // Use lazy loading pattern like native videos
        return `
            <div class="video-wrapper external-video-wrapper lazy-video" 
                 data-lazy-video
                 data-video-type="external"
                 data-video-host="${videoHost}"
                 data-video-embed-url="${embedUrl}"
                 data-video-poster="${previewImage}">
                <img src="${previewImage}" alt="${this.escapeHtml(product.title)}" class="video-placeholder">
                <div class="video-play-indicator" aria-hidden="true">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="32" cy="32" r="32" fill="rgba(255,255,255,0.95)"/>
                        <path d="M24 18L46 32L24 46V18Z" fill="#000"/>
                    </svg>
                </div>
            </div>
        `;
    }

    generateSlideshowHTML(product) {
        if (!product.media) {
            console.warn('[generateSlideshowHTML] No media found for product');
            return '';
        }

        const limit = this.config.slideshowLength || 3;
        let images = product.media.filter(m => m.media_type === 'image');


        if (images.length === 0) {
            console.warn('[generateSlideshowHTML] No images found in product media');
            return '';
        }

        images = images.slice(0, limit);

        let slidesHtml = '';
        images.forEach((media, index) => {
            // Use preview_image.src which contains the full Shopify CDN URL
            let src = media.preview_image?.src || media.src;

            if (!src) {
                console.warn('[generateSlideshowHTML] No src found for image', index);
                return;
            }

            // Ensure we have the full URL - add width parameter if not present
            if (!src.includes('?') && !src.includes('_800x')) {
                // Add Shopify image size parameter
                src = src + '?width=800';
            }

            const width = media.preview_image?.width || media.width || 800;
            const height = media.preview_image?.height || media.height || 800;
            const alt = media.alt || product.title;



            slidesHtml += `
                <img src="${src}" 
                     alt="${this.escapeHtml(alt)}" 
                     class="slideshow-slide${index === 0 ? ' active' : ''}"
                     loading="lazy"
                     width="${width}"
                     height="${height}">
            `;
        });

        const html = `
            <div class="slideshow-wrapper" data-slideshow-length="${limit}">
                ${slidesHtml}
            </div>
        `;


        return html;
    }

    generateImageHTML(product, media) {
        const previewImage = media?.preview_image?.src || product.featured_media?.preview_image?.src || '';

        if (!previewImage) {
            return '<div class="placeholder"></div>';
        }

        // Match Liquid lazyload pattern
        const lazyAttrs = this.config.enableLazyload
            ? `data-srcset="${previewImage}" loading="lazy" class="motion-reduce lazyload"`
            : `src="${previewImage}"`;

        return `<img ${lazyAttrs} alt="${this.escapeHtml(product.title)}" class="uniform-image">`;
    }

    generatePriceHTML(product) {
        const price = this.formatMoney(product.price);
        const comparePrice = product.compare_at_price > product.price ? this.formatMoney(product.compare_at_price) : null;

        if (comparePrice) {
            const discount = Math.round((1 - product.price / product.compare_at_price) * 100);
            return `
                <div class="price price--on-sale">
                    <dl>
                        <div class="price__regular">
                            <dd class="price__last">
                                <span class="price-item price-item--regular">${comparePrice}</span>
                            </dd>
                        </div>
                        <div class="price__sale">
                            <dd class="price__compare" data-compare="${product.compare_at_price}">
                                <s class="price-item price-item--regular">${comparePrice}</s>
                            </dd>
                            <dd class="price__last price_percent" data-last="${product.price}">
                                <span class="price-item price-item--sale">${price}</span>
                            </dd>
                            <dd class="price__label_sale price__label_percent">
                                <span class="label_sale label_sale_percent">(-${discount}%)</span>
                            </dd>
                        </div>
                        <small class="unit-price caption hidden">
                            <dt class="visually-hidden">Unit price</dt>
                            <dd class="price__last"><span></span><span aria-hidden="true">/</span><span class="visually-hidden">&nbsp;per&nbsp;</span><span></span></dd>
                        </small>
                    </dl>
                </div>
            `;
        }

        return `
            <div class="price">
                <dl>
                    <div class="price__regular">
                        <dd class="price__last">
                            <span class="price-item price-item--regular">${price}</span>
                        </dd>
                    </div>
                </dl>
            </div>
        `;
    }

    formatMoney(cents) {
        // Basic money formatting - you may want to use Shopify's money format
        return `$${(cents / 100).toFixed(2)}`;
    }

    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

// Register the custom element
if (!customElements.get('product-reel-lazy-loader')) {
    customElements.define('product-reel-lazy-loader', ProductReelLazyLoader);
}
